import React, { useState, useEffect } from "react";
import { getRequest, postRequest } from "../../../helpers/ApiHelpers";
import { css } from "emotion";
import { Button, Header, Grid, Form, TextArea, Input, Checkbox, Dropdown } from "semantic-ui-react";
import { withRouter } from "react-router-dom";
import shortid from "shortid";
import { readFileAsBase64 } from "../../../helpers/ReadFileAsBase64";
import FileUpload from "../../FileUpload";
import { logEvent } from "../../../helpers/analyticsLogger";

export function EditAddPolicyEndorsementNote({ match, history }) {
  const [state, setState] = useState({
    submitAttempted: false,
    submitting: false,
    policyEndorsementNote: {},
    editMode: match.path.includes("edit"),
    errorList: [],
    files: [],
    filesToAdd: [],
    filesToDelete: []
  });

  const [underwriters, setUnderwriters] = useState([]);
  const [selectedUnderwriter, setSelectedUnderwriter] = useState(state.editMode ? null : +match.params.underwriter);

  const selectedState = +match.params.state;
  let stateId = selectedState;

  async function getPolicyEndorsementNoteById(id) {
    const result = await getRequest(`/policyEndorsement/getPolicyEndorsementNoteById`, {
      id
    });
    if (result.success) {
      setState(state => ({
        ...state,
        policyEndorsementNote: result.data
      }));
      setSelectedUnderwriter(result.data.underwriterId);
    }
  }

  async function getUnderwriters() {
    const result = await getRequest(`/underwriter/getStateUnderwriters?id=${+match.params.state || 31}`);
    if (result.success) {
      const underwriters = result.data
        .filter(u => u.name !== "All Underwriters")
        .map(u => ({
          key: u.id,
          value: u.id,
          text: u.name
        }));
      setUnderwriters(underwriters);
    }
  }

  useEffect(() => {
    getUnderwriters();
    if (state.editMode) {
      getPolicyEndorsementNoteById(match.params.id);
    }
  }, []);

  async function saveChanges() {
    setState(state => ({
      ...state,
      submitting: true
    }));
    const result = await postRequest(`/policyEndorsement/editAddPolicyEndorsementNote`, {
      ...state.policyEndorsementNote,
      stateId: state.editMode ? state.policyEndorsementNote.stateId : stateId,
      underwriterId: selectedUnderwriter,
      filesToAdd: await Promise.all(
        state.filesToAdd.map(async f => ({
          base64File: await readFileAsBase64(f),
          fileName: f.name
        }))
      ),
      filesToDelete: state.filesToDelete
    });
    if (result.success) {
      logEvent(null, state.editMode ? "Edited" : "Added", "Policy BI Note");
      history.goBack();
    }
  }

  return (
    <div className={css({ width: 605, paddingBottom: 34 })}>
      <Header style={{ fontSize: 24, marginBottom: 41 }} as="h3">
        Policy Endorsement Note
      </Header>
      <Form>
        <Grid>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <label>Underwriter</label>
                <Dropdown
                  size="large"
                  fluid
                  selection
                  placeholder="Select Underwriter"
                  style={{ fontSize: 16 }}
                  options={underwriters}
                  value={selectedUnderwriter}
                  onChange={(_, { value }) => setSelectedUnderwriter(value)}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <label>Subject</label>
                <Input
                  placeholder="Subject"
                  value={state.policyEndorsementNote.subject}
                  onChange={(_, { value }) =>
                    setState(state => ({
                      ...state,
                      policyEndorsementNote: {
                        ...state.policyEndorsementNote,
                        subject: value
                      }
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Note</label>
                <TextArea
                  placeholder="Note"
                  style={{ minHeight: 292 }}
                  value={state.policyEndorsementNote.note}
                  onChange={(_, { value }) =>
                    setState(state => ({
                      ...state,
                      policyEndorsementNote: {
                        ...state.policyEndorsementNote,
                        note: value
                      }
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <FileUpload
                  fileList={state.filesToAdd}
                  setFileToApp={filesToAdd =>
                    setState(state => ({
                      ...state,
                      filesToAdd: [...state.filesToAdd, ...filesToAdd]
                    }))
                  }
                  deleteFileToApp={fileName =>
                    setState(state => ({
                      ...state,
                      filesToAdd: [...state.filesToAdd.filter(f => f.name !== fileName)]
                    }))
                  }
                  multiple={true}
                />
              </Form.Field>
              {state.policyEndorsementNote.files &&
                state.policyEndorsementNote.files.map(f => (
                  <p
                    key={shortid.generate()}
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        filesToDelete: [...state.filesToDelete, f.id],
                        policyEndorsementNote: {
                          ...state.policyEndorsementNote,
                          files: [...state.policyEndorsementNote.files.filter(nf => nf.id !== f.id)]
                        }
                      }))
                    }
                    className={css({
                      "&:hover": { color: "#707898" },
                      cursor: "pointer"
                    })}
                  >
                    <i className="fal fa-trash-alt" style={{ marginRight: 8 }} />
                    {f.fileName}
                  </p>
                ))}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>
                  Highlight Row
                  <Checkbox
                    checked={state.policyEndorsementNote.highlight}
                    onChange={() => {
                      setState(state => ({
                        ...state,
                        policyEndorsementNote: {
                          ...state.policyEndorsementNote,
                          highlight: !state.policyEndorsementNote.highlight
                        }
                      }));
                    }}
                    toggle
                    className={css({
                      verticalAlign: "bottom !important",
                      marginLeft: "8px !important",
                      "&.ui.toggle.checkbox input:checked ~ .box:before, &.ui.toggle.checkbox input:checked ~ label:before": {
                        backgroundColor: "#00b8d4 !important"
                      }
                    })}
                  />
                </label>
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column floated="right" className={css({ textAlign: "right" })}>
              <Button size="large" className="cancelButton" onClick={() => history.goBack()}>
                Cancel
              </Button>
              <Button size="large" className="saveButton" onClick={saveChanges}>
                {state.submitting ? "Submitting..." : "Save"}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </div>
  );
}

withRouter(EditAddPolicyEndorsementNote);
